import React, { memo, useCallback, useEffect, useRef } from "react";
import { FilePond, registerPlugin } from "react-filepond";
import { FileOrigin, FileStatus } from "filepond";
import "filepond/dist/filepond.min.css";
import FilePondPluginFileValidateType from "filepond-plugin-file-validate-type";

registerPlugin(FilePondPluginFileValidateType);

const ADOBE_FILE_TYPES = {
    ai: "application/postscript",
    ait: "application/postscript",
    eps: "application/postscript",
    psd: "application/x-photoshop",
    psb: "application/x-photoshop",
    pdd: "application/x-photoshop",
    psdt: "application/x-photoshop",
    xd: "application/vnd.adobe.xd",
    indd: "application/x-indesign",
    indt: "application/x-indesign",
    indb: "application/x-indesign",
    indl: "application/x-indesign",
    inx: "application/x-indesign",
    idml: "application/vnd.adobe.indesign-idml-package",
    icml: "application/vnd.adobe.incopy-icml",
    incx: "application/vnd.adobe.incopy",
    aep: "application/vnd.adobe.aftereffects.project",
    aepx: "application/vnd.adobe.aftereffects.project",
    aet: "application/vnd.adobe.aftereffects.template",
    ffx: "application/vnd.adobe.aftereffects.preset",
    prproj: "application/vnd.adobe.premiere.project",
    prel: "application/vnd.adobe.premiere.elements-project",
    prtl: "application/vnd.adobe.premiere.title",
    mogrt: "application/vnd.adobe.mogrt",
    epr: "application/vnd.adobe.media-encoder.preset",
    fla: "application/vnd.adobe.fla",
    xfl: "application/vnd.adobe.xfl",
    fxg: "application/vnd.adobe.fxg+xml",
    sesx: "application/vnd.adobe.audition.session",
    dn: "application/vnd.adobe.dimension",
    sbsar: "application/vnd.adobe.substance.sbsar",
    sbs: "application/vnd.adobe.substance.sbs",
    spp: "application/vnd.adobe.substance.spp",
    spsm: "application/vnd.adobe.substance.spsm",
    abr: "application/vnd.adobe.photoshop.brush",
    asl: "application/vnd.adobe.photoshop.style",
    grd: "application/vnd.adobe.photoshop.gradient",
    pat: "application/vnd.adobe.photoshop.pattern",
    acv: "application/vnd.adobe.photoshop.curves",
    atn: "application/vnd.adobe.photoshop.action",
    ase: "application/vnd.adobe.ase",
    aco: "application/vnd.adobe.aco",
    act: "application/vnd.adobe.act",
    dng: "application/x-adobe-dng",
    dcp: "application/vnd.adobe.dcp",
    xmp: "application/rdf+xml",
    lrtemplate: "application/vnd.adobe.lightroom.template",
    xdp: "application/vnd.adobe.xdp+xml",
    xfdf: "application/vnd.adobe.xfdf",
    fdf: "application/vnd.fdf",
};

const ADOBE_DECLARED_TYPES = new Set([
    "",
    "application/octet-stream",
    "binary/octet-stream",
    "application/x-octet-stream",
    "application/illustrator",
    "application/x-illustrator",
    "application/eps",
    "application/x-eps",
    "image/eps",
    "image/x-eps",
    "application/x-photoshop",
    "application/photoshop",
    "application/psd",
    "image/psd",
    "image/x-photoshop",
    "image/vnd.adobe.photoshop",
    "image/x-adobe-dng",
    "application/x-indesign",
    "image/x-dcraw",
    "application/zip",
    "application/x-zip-compressed",
    "application/xml",
    "text/xml",
    ...Object.values(ADOBE_FILE_TYPES),
]);

const detectFileType = (file, type) => {
    const extension = (/\.([a-z0-9]+)$/i.exec(file.name || "") || [])[1]?.toLowerCase();
    const declared = (type || "").split(";")[0].trim().toLowerCase();
    const adobeType = extension && Object.prototype.hasOwnProperty.call(ADOBE_FILE_TYPES, extension) && ADOBE_DECLARED_TYPES.has(declared) ? ADOBE_FILE_TYPES[extension] : null;
    return Promise.resolve(adobeType || type);
};

const ACCEPTED_FILE_TYPES = [
    // Images
    "image/*",

    // Documents
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "application/vnd.oasis.opendocument.text",
    "application/vnd.oasis.opendocument.spreadsheet",
    "application/vnd.oasis.opendocument.presentation",
    "text/plain",
    "text/rtf",
    "text/html",
    "text/css",
    "text/javascript",
    "application/json",
    "application/xml",
    "text/xml",
    "text/csv",
    "application/csv",

    // Video files
    "video/*",
    "video/mp4",
    "video/mpeg",
    "video/quicktime",
    "video/x-msvideo",
    "video/x-ms-wmv",
    "video/x-flv",
    "video/webm",
    "video/ogg",
    "video/3gpp",
    "video/3gpp2",

    // Compressed files
    "application/x-rar-compressed",
    "application/x-7z-compressed",
    "application/x-gtar",
    "application/zip",
    "application/gzip",
    "application/x-bzip2",
    "application/x-tar",
    "application/x-compressed",
    "application/x-zip-compressed",

    // Other common formats
    "application/epub+zip",
    "application/x-mobipocket-ebook",
    "application/vnd.amazon.ebook",
    "application/x-font-ttf",
    "application/x-font-otf",
    "application/x-font-woff",
    "application/x-font-woff2",
    "application/vnd.ms-fontobject",
    "application/x-sql",
    "application/x-yaml",
    "application/x-toml",
    "application/x-markdown",
    "text/markdown",

    // Adobe
    ...new Set(Object.values(ADOBE_FILE_TYPES)),
    ...Object.keys(ADOBE_FILE_TYPES).map((extension) => `.${extension}`),
];

const DRAG_EVENTS = ["dragenter", "dragover", "dragleave", "drop"];

const dropEmptyDragEvent = (e) => {
    if (!e.dataTransfer) e.stopPropagation();
};

const isItemBusy = (item) => {
    switch (item.status) {
        case FileStatus.INIT:
        case FileStatus.LOADING:
        case FileStatus.PROCESSING_QUEUED:
        case FileStatus.PROCESSING:
            return true;
        case FileStatus.IDLE:
            return item.origin === FileOrigin.INPUT;
        default:
            return false;
    }
};

const ERROR_STATUSES = new Set([FileStatus.LOAD_ERROR, FileStatus.PROCESSING_ERROR, FileStatus.PROCESSING_REVERT_ERROR]);

const isItemError = (item) => ERROR_STATUSES.has(item.status);

const hasIncomingFiles = (e) => (e.type === "drop" ? e.dataTransfer?.files?.length > 0 : e.target?.type === "file" && e.target.files?.length > 0);

const RECONCILE_DELAY_MS = 1000;

const FilePondUploader = memo(({ setAttachments, files=[], onUploadStatusChange, onErrorStateChange, allowMultiple = true, acceptedFileTypes = ACCEPTED_FILE_TYPES }) => {
    const wrapperRef = useRef(null);
    const pondRef = useRef(null);
    const reconcileTimer = useRef(null);
    const statusCallbacks = useRef({ onUploadStatusChange, onErrorStateChange });
    statusCallbacks.current = { onUploadStatusChange, onErrorStateChange };

    const reportStatus = useCallback((fileItems) => {
        const { onUploadStatusChange: onBusy, onErrorStateChange: onError } = statusCallbacks.current;
        if (!onBusy && !onError) return;
        const items = fileItems || pondRef.current?.getFiles?.() || [];
        onBusy?.(items.some(isItemBusy));
        onError?.(items.some(isItemError));
    }, []);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return undefined;
        const markIncoming = (e) => {
            if (!hasIncomingFiles(e)) return;
            statusCallbacks.current.onUploadStatusChange?.(true);
            clearTimeout(reconcileTimer.current);
            reconcileTimer.current = setTimeout(() => reportStatus(), RECONCILE_DELAY_MS);
        };
        DRAG_EVENTS.forEach((type) => wrapper.addEventListener(type, dropEmptyDragEvent, true));
        wrapper.addEventListener("drop", markIncoming, true);
        wrapper.addEventListener("change", markIncoming, true);
        return () => {
            DRAG_EVENTS.forEach((type) => wrapper.removeEventListener(type, dropEmptyDragEvent, true));
            wrapper.removeEventListener("drop", markIncoming, true);
            wrapper.removeEventListener("change", markIncoming, true);
            clearTimeout(reconcileTimer.current);
            statusCallbacks.current.onUploadStatusChange?.(false);
            statusCallbacks.current.onErrorStateChange?.(false);
        };
    }, [reportStatus]);

    const handleUpload = useCallback(
        (fileItems) => {
            setAttachments(fileItems);
            reportStatus(fileItems);
        },
        [setAttachments, reportStatus],
    );

    const handleItemEvent = useCallback(() => reportStatus(), [reportStatus]);

    return (
        <div ref={wrapperRef}>
            <FilePond
                ref={pondRef}
                files={files}
                allowMultiple={allowMultiple}
                name="file"
                credits={false}
                server={{
                    process: {
                        url: "/api/attachment/upload",
                        method: "POST",
                    },
                    revert: {
                        url: "/api/attachment/upload",
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json; charset=utf-8",
                        },
                    },
                }}
                onupdatefiles={handleUpload}
                onaddfilestart={handleItemEvent}
                onaddfile={handleItemEvent}
                onerror={handleItemEvent}
                onprocessfilestart={handleItemEvent}
                onprocessfile={handleItemEvent}
                onprocessfileabort={handleItemEvent}
                onprocessfilerevert={handleItemEvent}
                onprocessfiles={handleItemEvent}
                acceptedFileTypes={acceptedFileTypes}
                fileValidateTypeDetectType={detectFileType}
            />
        </div>
    );
});

FilePondUploader.displayName = 'FilePondUploader';

export default FilePondUploader;
